/**
 * Pose evaluation for the procedural rig. Rest bone frames are axis-aligned at the joint (identity orientation), so
 * rotations are authored in model axes. Every bone also carries a non-inherited deformation scale (length along the
 * bone, thickness across it): the bone's own vertices are scaled, and its children's joints move with the scaled
 * offset, but children never inherit a non-uniform scale. That keeps proportion morphs (head size, belly, limb length,
 * tail length) clean on any base body.
 *
 *   W_root = T(head_root + rootOffset) · R_root
 *   W_i    = W_parent · T(S_parent · (head_i − head_parent)) · R_i
 *   skin_i = W_i · S_i · T(−head_i)
 */
import type { Skeleton, V3 } from './rig';

export type Quat = [number, number, number, number];

export interface BonePose {
  rot: Quat;
  /** Rigid attachment translation in its parent frame; absent means zero. */
  offset?: V3;
  /** Scale along the bone direction. */
  length: number;
  /** Scale across the bone (x/z-ish thickness), uniform in the plane perpendicular to the bone. */
  thick: number;
}

export interface Pose { bones: BonePose[]; rootOffset: V3 }

export const qIdentity = (): Quat => [0, 0, 0, 1];
export function qAxisAngle(ax: number, ay: number, az: number, angle: number): Quat {
  const l = Math.hypot(ax, ay, az) || 1, s = Math.sin(angle / 2);
  return [(ax / l) * s, (ay / l) * s, (az / l) * s, Math.cos(angle / 2)];
}
export function qMul(a: Quat, b: Quat): Quat {
  return [
    a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
    a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
    a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
    a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
  ];
}
/** Euler XYZ (radians) in model axes. */
export function qEuler(x: number, y: number, z: number): Quat {
  return qMul(qMul(qAxisAngle(0, 0, 1, z), qAxisAngle(0, 1, 0, y)), qAxisAngle(1, 0, 0, x));
}

export function restPose(sk: Skeleton): Pose {
  return { bones: sk.bones.map(() => ({ rot: qIdentity(), length: 1, thick: 1 })), rootOffset: [0, 0, 0] };
}

// 3x4 affine stored as 12 floats (column-major 3x3 + translation) during evaluation
type Aff = Float64Array;
const aff = (): Aff => new Float64Array(12);
function affFromRot(q: Quat, out: Aff): Aff {
  const [x, y, z, w] = q;
  out[0] = 1 - 2 * (y * y + z * z); out[1] = 2 * (x * y + w * z); out[2] = 2 * (x * z - w * y);
  out[3] = 2 * (x * y - w * z); out[4] = 1 - 2 * (x * x + z * z); out[5] = 2 * (y * z + w * x);
  out[6] = 2 * (x * z + w * y); out[7] = 2 * (y * z - w * x); out[8] = 1 - 2 * (x * x + y * y);
  out[9] = 0; out[10] = 0; out[11] = 0;
  return out;
}
function affMul(a: Aff, b: Aff, out: Aff = aff()): Aff {
  const r = aff();
  for (let c = 0; c < 3; c++) for (let row = 0; row < 3; row++) r[c * 3 + row] = a[row] * b[c * 3] + a[3 + row] * b[c * 3 + 1] + a[6 + row] * b[c * 3 + 2];
  for (let row = 0; row < 3; row++) r[9 + row] = a[row] * b[9] + a[3 + row] * b[10] + a[6 + row] * b[11] + a[9 + row];
  out.set(r);
  return out;
}
function affApply(a: Aff, p: V3): V3 {
  return [a[0] * p[0] + a[3] * p[1] + a[6] * p[2] + a[9], a[1] * p[0] + a[4] * p[1] + a[7] * p[2] + a[10], a[2] * p[0] + a[5] * p[1] + a[8] * p[2] + a[11]];
}
/** Deformation scale S = thick·I + (length − thick)·u uᵀ about the origin. */
function affScale(u: V3, length: number, thick: number, out: Aff): Aff {
  out.fill(0);
  for (let c = 0; c < 3; c++) for (let r = 0; r < 3; r++) out[c * 3 + r] = (r === c ? thick : 0) + (length - thick) * u[r] * u[c];
  return out;
}

export interface EvalResult {
  /** Column-major 4x4 skin matrices, 16 floats per bone. */
  skin: Float32Array;
  /** Posed joint positions (for overlays / attachments). */
  joints: V3[];
  /** Posed bone world frames (affine 3x4), for rigid attachments. */
  world: Aff[];
}

export function evaluate(sk: Skeleton, pose: Pose, out?: EvalResult): EvalResult {
  const B = sk.bones.length;
  const res: EvalResult = out ?? { skin: new Float32Array(B * 16), joints: [], world: [] };
  const W: Aff[] = [];
  const S: Aff[] = [];
  const tmp = aff(), rot = aff();
  for (let i = 0; i < B; i++) {
    const b = sk.bones[i], bp = pose.bones[i];
    const dir: V3 = [b.tail[0] - b.head[0], b.tail[1] - b.head[1], b.tail[2] - b.head[2]];
    const len = Math.hypot(dir[0], dir[1], dir[2]) || 1;
    const u: V3 = [dir[0] / len, dir[1] / len, dir[2] / len];
    S[i] = affScale(u, bp.length, bp.thick, aff());
    affFromRot(bp.rot, rot);
    let w: Aff;
    if (b.parent < 0) {
      w = aff(); w.set(rot);
      w[9] = b.head[0] + pose.rootOffset[0]; w[10] = b.head[1] + pose.rootOffset[1]; w[11] = b.head[2] + pose.rootOffset[2];
    } else {
      const pb = sk.bones[b.parent];
      const off: V3 = [b.head[0] - pb.head[0], b.head[1] - pb.head[1], b.head[2] - pb.head[2]];
      const so = affApply(S[b.parent], off);
      if(bp.offset)for(let axis=0;axis<3;axis++)so[axis]+=bp.offset[axis];
      tmp.fill(0); tmp[0] = tmp[4] = tmp[8] = 1; tmp[9] = so[0]; tmp[10] = so[1]; tmp[11] = so[2];
      w = affMul(affMul(W[b.parent], tmp), rot);
    }
    W[i] = w;
    // skin = W · S · T(−head)
    const ws = affMul(w, S[i]);
    const t = affApply(ws, [-b.head[0], -b.head[1], -b.head[2]]);
    const o = i * 16;
    res.skin[o] = ws[0]; res.skin[o + 1] = ws[1]; res.skin[o + 2] = ws[2]; res.skin[o + 3] = 0;
    res.skin[o + 4] = ws[3]; res.skin[o + 5] = ws[4]; res.skin[o + 6] = ws[5]; res.skin[o + 7] = 0;
    res.skin[o + 8] = ws[6]; res.skin[o + 9] = ws[7]; res.skin[o + 10] = ws[8]; res.skin[o + 11] = 0;
    res.skin[o + 12] = t[0] - ws[9] + ws[9]; res.skin[o + 13] = t[1]; res.skin[o + 14] = t[2]; res.skin[o + 15] = 1;
    res.joints[i] = [w[9], w[10], w[11]];
    res.world[i] = w;
  }
  return res;
}

/** Posed tail point of bone i (for overlays). */
export function boneTip(sk: Skeleton, pose: Pose, ev: EvalResult, i: number): V3 {
  const b = sk.bones[i];
  const off: V3 = [b.tail[0] - b.head[0], b.tail[1] - b.head[1], b.tail[2] - b.head[2]];
  const len = Math.hypot(off[0], off[1], off[2]) || 1;
  const s = pose.bones[i].length;
  const w = ev.world[i];
  const p: V3 = [off[0] * s, off[1] * s, off[2] * s];
  void len;
  return [w[0] * p[0] + w[3] * p[1] + w[6] * p[2] + w[9], w[1] * p[0] + w[4] * p[1] + w[7] * p[2] + w[10], w[2] * p[0] + w[5] * p[1] + w[8] * p[2] + w[11]];
}
